import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, NewsItem, TrendItem, PressRelease, FinancialReport, FinancialConcept } from '../api';
import { useAuth } from '../AuthContext';

const PERIODS = ['daily', 'weekly', 'monthly', 'yearly'];
const DATA_TABS = ['News', 'Trends', 'Press Releases', 'Financial Reports'];
const TAB_SLUGS: Record<string, string> = { 'news': 'News', 'trends': 'Trends', 'press-releases': 'Press Releases', 'financial-reports': 'Financial Reports' };
const TAB_TO_SLUG: Record<string, string> = { 'News': 'news', 'Trends': 'trends', 'Press Releases': 'press-releases', 'Financial Reports': 'financial-reports' };

export default function DataSummary() {
  const { symbol: urlSymbol, period: urlPeriod, tab: urlTab } = useParams<{ symbol?: string; period?: string; tab?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [symbols, setSymbols] = useState<string[]>([]);
  const [symbol, setSymbol] = useState(urlSymbol?.toUpperCase() || '');
  const [period, setPeriod] = useState(
    urlPeriod && PERIODS.includes(urlPeriod) ? urlPeriod : 'daily'
  );
  const [tab, setTab] = useState(
    urlTab && TAB_SLUGS[urlTab] ? TAB_SLUGS[urlTab] : 'News'
  );
  const [news, setNews] = useState<NewsItem[]>([]);
  const [trends, setTrends] = useState<TrendItem[]>([]);
  const [pressReleases, setPressReleases] = useState<PressRelease[]>([]);
  const [financialReports, setFinancialReports] = useState<FinancialReport[]>([]);
  const [reportFreq, setReportFreq] = useState<'quarterly' | 'annual'>('quarterly');
  const [reportsLoading, setReportsLoading] = useState(false);
  const [expandedReport, setExpandedReport] = useState<string | null>(null);
  const [reportSection, setReportSection] = useState<'incomeStatement' | 'balanceSheet' | 'cashFlow'>('incomeStatement');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  useEffect(() => {
    const s = user?.stocks || [];
    setSymbols(s);
    if (!symbol && s.length > 0) setSymbol(s[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.stocks]);

  useEffect(() => {
    if (symbol) {
      navigate(`/data/${symbol}/${period}/${TAB_TO_SLUG[tab] || 'news'}`, { replace: true });
    }
  }, [symbol, period, tab, navigate]);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    Promise.all([
      api.getNews(symbol, period),
      api.getTrends(symbol, period),
      api.getPressReleases(symbol, period),
    ])
      .then(([n, t, p]) => {
        setNews(n);
        setTrends(t);
        setPressReleases(p);
      })
      .finally(() => setLoading(false));
  }, [symbol, period]);

  useEffect(() => {
    if (!symbol || tab !== 'Financial Reports') return;
    setReportsLoading(true);
    api.getFinancialReports(symbol, reportFreq)
      .then(setFinancialReports)
      .catch(() => setFinancialReports([]))
      .finally(() => setReportsLoading(false));
  }, [symbol, reportFreq, tab]);

  const formatValue = (value: number, unit: string) => {
    if (unit === 'USD' || unit === 'usd') {
      if (Math.abs(value) >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
      if (Math.abs(value) >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
      if (Math.abs(value) >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
      return `$${value.toLocaleString()}`;
    }
    if (unit === 'shares') return `${(value / 1e6).toFixed(2)}M shares`;
    return value.toLocaleString();
  };

  const sentimentCounts = (items: { sentiment: string }[]) => {
    const counts = { positive: 0, negative: 0, neutral: 0 };
    items.forEach((i) => {
      const s = (i.sentiment || 'neutral').toLowerCase();
      if (s in counts) counts[s as keyof typeof counts]++;
    });
    return counts;
  };

  const newsSentiments = sentimentCounts(news);
  const prSentiments = sentimentCounts(pressReleases);

  const googleNewsCount = news.filter((n) => n.category === 'google-news').length;
  const finnhubCount = news.length - googleNewsCount;

  const q = search.toLowerCase();
  const filteredNews = q
    ? news.filter((n) =>
        [n.summary, n.source, n.sentiment, n.date].some((f) => f?.toLowerCase().includes(q))
      )
    : news;
  const filteredTrends = q
    ? trends.filter((t) =>
        [t.period, String(t.buy), String(t.hold), String(t.sell), String(t.strongBuy), String(t.strongSell)].some((f) => f?.toLowerCase().includes(q))
      )
    : trends;
  const filteredPR = q
    ? pressReleases.filter((p) =>
        [p.title, p.summary, p.sentiment, p.date].some((f) => f?.toLowerCase().includes(q))
      )
    : pressReleases;

  return (
    <div>
      <h2>Data Summary</h2>

      <div className="controls">
        <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
          {symbols.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select value={period} onChange={(e) => setPeriod(e.target.value)}>
          {PERIODS.map((p) => (
            <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search news, press releases..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            border: '1px solid #2a2d3a',
            background: '#1a1d27',
            color: '#e1e4eb',
            fontSize: 14,
            minWidth: 220,
          }}
        />
      </div>

      {/* Summary cards */}
      <div className="card-grid">
        <div className="card">
          <h3>News ({news.length})</h3>
          <div className="sentiment-bar">
            <span style={{ color: 'var(--green)' }}>+{newsSentiments.positive}</span>
            <span style={{ color: 'var(--red)' }}>-{newsSentiments.negative}</span>
            <span style={{ color: 'var(--yellow)' }}>~{newsSentiments.neutral}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, display: 'flex', gap: 12 }}>
            <span>Finnhub: {finnhubCount}</span>
            <span>Google News: {googleNewsCount}</span>
          </div>
        </div>
        <div className="card">
          <h3>Trends ({trends.length})</h3>
          {trends.length > 0 && (
            <div className="sentiment-bar">
              <span style={{ color: 'var(--green)' }}>Buy: {trends.reduce((a, t) => a + (t.buy || 0) + (t.strongBuy || 0), 0)}</span>
              <span style={{ color: 'var(--red)' }}>Sell: {trends.reduce((a, t) => a + (t.sell || 0) + (t.strongSell || 0), 0)}</span>
              <span style={{ color: 'var(--yellow)' }}>Hold: {trends.reduce((a, t) => a + (t.hold || 0), 0)}</span>
            </div>
          )}
        </div>
        <div className="card">
          <h3>Press Releases ({pressReleases.length})</h3>
          <div className="sentiment-bar">
            <span style={{ color: 'var(--green)' }}>+{prSentiments.positive}</span>
            <span style={{ color: 'var(--red)' }}>-{prSentiments.negative}</span>
            <span style={{ color: 'var(--yellow)' }}>~{prSentiments.neutral}</span>
          </div>
        </div>
      </div>

      {/* Detail tabs */}
      <div className="tabs">
        {DATA_TABS.map((t) => (
          <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading data...</div>
      ) : (
        <div className="card">
          {tab === 'News' && (
            <table className="summary-table">
              <thead>
                <tr><th></th><th>Date</th><th>Origin</th><th>Source</th><th>Summary</th><th>Sentiment</th></tr>
              </thead>
              <tbody>
                {filteredNews.map((n, i) => (
                  <React.Fragment key={n.id || i}>
                    <tr onClick={() => n.data && toggleExpand(n.id || String(i))} style={{ cursor: n.data ? 'pointer' : 'default' }}>
                      <td style={{ width: 28, fontSize: 12 }}>{n.data ? (expandedId === (n.id || String(i)) ? '▼' : '▶') : ''}</td>
                      <td>{n.date}</td>
                      <td><span style={{
                        fontSize: 11,
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: n.category === 'google-news' ? '#1a73e833' : '#34a85333',
                        color: n.category === 'google-news' ? '#8ab4f8' : '#81c995',
                      }}>{n.category === 'google-news' ? 'Google' : 'Finnhub'}</span></td>
                      <td>{n.source}</td>
                      <td>
                        {n.url ? (
                          <a href={n.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }} onClick={(e) => e.stopPropagation()}>
                            {n.summary?.slice(0, 120)}
                          </a>
                        ) : (
                          n.summary?.slice(0, 120)
                        )}
                      </td>
                      <td><span className={`badge ${n.sentiment?.toLowerCase()}`}>{n.sentiment}</span></td>
                    </tr>
                    {expandedId === (n.id || String(i)) && n.data && (
                      <tr>
                        <td colSpan={6} className="expanded-content">{n.data}</td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {filteredNews.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No news for this period</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'Trends' && (
            <table className="summary-table">
              <thead>
                <tr><th>Period</th><th>Strong Buy</th><th>Buy</th><th>Hold</th><th>Sell</th><th>Strong Sell</th></tr>
              </thead>
              <tbody>
                {filteredTrends.map((t, i) => (
                  <tr key={i}>
                    <td>{t.period}</td>
                    <td style={{ color: 'var(--green)' }}>{t.strongBuy}</td>
                    <td style={{ color: 'var(--green)' }}>{t.buy}</td>
                    <td style={{ color: 'var(--yellow)' }}>{t.hold}</td>
                    <td style={{ color: 'var(--red)' }}>{t.sell}</td>
                    <td style={{ color: 'var(--red)' }}>{t.strongSell}</td>
                  </tr>
                ))}
                {filteredTrends.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No trends for this period</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'Press Releases' && (
            <table className="summary-table">
              <thead>
                <tr><th></th><th>Date</th><th>Title / Summary</th><th>Sentiment</th></tr>
              </thead>
              <tbody>
                {filteredPR.map((p, i) => {
                  const hasDetail = !!(p.data || p.summary);
                  const detailContent = p.data || p.summary;
                  return (
                  <React.Fragment key={p.id || i}>
                    <tr onClick={() => hasDetail && toggleExpand(p.id || `pr-${i}`)} style={{ cursor: hasDetail ? 'pointer' : 'default' }}>
                      <td style={{ width: 28, fontSize: 12 }}>{hasDetail ? (expandedId === (p.id || `pr-${i}`) ? '▼' : '▶') : ''}</td>
                      <td>{p.date}</td>
                      <td>{p.title || p.summary?.slice(0, 120)}</td>
                      <td><span className={`badge ${p.sentiment?.toLowerCase()}`}>{p.sentiment}</span></td>
                    </tr>
                    {expandedId === (p.id || `pr-${i}`) && detailContent && (
                      <tr>
                        <td colSpan={4} className="expanded-content">{detailContent}</td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
                {filteredPR.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No press releases for this period</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'Financial Reports' && (
            <div>
              <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                <select
                  value={reportFreq}
                  onChange={(e) => setReportFreq(e.target.value as 'quarterly' | 'annual')}
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: '1px solid #2a2d3a',
                    background: '#1a1d27',
                    color: '#e1e4eb',
                    fontSize: 14,
                  }}
                >
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(['incomeStatement', 'balanceSheet', 'cashFlow'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setReportSection(s)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: 8,
                        border: reportSection === s ? '1px solid var(--accent)' : '1px solid #2a2d3a',
                        background: reportSection === s ? 'var(--accent)' : 'transparent',
                        color: reportSection === s ? '#fff' : '#e1e4eb',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      {s === 'incomeStatement' ? 'Income Statement' : s === 'balanceSheet' ? 'Balance Sheet' : 'Cash Flow'}
                    </button>
                  ))}
                </div>
              </div>
              {reportsLoading ? (
                <div className="loading">Loading financial reports...</div>
              ) : financialReports.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>No financial reports available</div>
              ) : (
                financialReports.map((report, idx) => {
                  const reportId = `report-${report.year}-${report.quarter}`;
                  const isExpanded = expandedReport === reportId;
                  const section = report[reportSection];
                  return (
                    <div key={idx} style={{ marginBottom: 12, border: '1px solid #2a2d3a', borderRadius: 8, overflow: 'hidden' }}>
                      <div
                        onClick={() => setExpandedReport(isExpanded ? null : reportId)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: '#1a1d27',
                        }}
                      >
                        <div>
                          <strong>{report.form}</strong>
                          <span style={{ marginLeft: 12, color: 'var(--text-muted)' }}>
                            {report.year} Q{report.quarter}
                          </span>
                          <span style={{ marginLeft: 12, color: 'var(--text-muted)', fontSize: 13 }}>
                            Filed: {report.filedDate}
                          </span>
                        </div>
                        <span style={{ fontSize: 12 }}>{isExpanded ? '▼' : '▶'}</span>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: '0 16px 16px' }}>
                          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
                            Period: {report.startDate} to {report.endDate}
                          </div>
                          <table className="summary-table">
                            <thead>
                              <tr><th>Item</th><th style={{ textAlign: 'right' }}>Value</th><th>Unit</th></tr>
                            </thead>
                            <tbody>
                              {section.map((item: FinancialConcept, i: number) => (
                                <tr key={i}>
                                  <td>{item.label}</td>
                                  <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                                    {formatValue(item.value, item.unit)}
                                  </td>
                                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{item.unit}</td>
                                </tr>
                              ))}
                              {section.length === 0 && (
                                <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No data available for this section</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
